import * as React from 'react';
import {
  BorderStyle,
  Box,
  BoxProps,
  Button,
  ButtonColorVariant,
  ButtonProps,
  ButtonVariant,
  Color,
  Display,
  DisplayPosition,
  withCitrusTheme,
} from '@gousto-internal/citrus-react';

const initialAccordionContextValue = { isExpanded: false, contentId: '', buttonId: '' };
const AccordionContext = React.createContext({
  value: initialAccordionContextValue,
  setIsExpanded: (_isExpanded: boolean) => {},
});

const StyledAccordionButton = withCitrusTheme(Button, () => ({ boxShadow: 'none' }));

/**
 * TODO - move to citrus as a new variant
 */
export function AccordionButton({
  children,
  disabled = false,
  ...buttonProps
}: {
  children: React.ReactNode;
  disabled?: boolean;
} & Omit<ButtonProps, 'children'>) {
  const {
    value: { buttonId, contentId, isExpanded },
    setIsExpanded,
  } = React.useContext(AccordionContext);

  function toggle() {
    setIsExpanded(!isExpanded);
  }

  return (
    <StyledAccordionButton
      id={buttonId}
      aria-controls={contentId}
      aria-expanded={isExpanded}
      onClick={toggle}
      variant={ButtonVariant.Icon}
      colorVariant={ButtonColorVariant.Secondary}
      icon={isExpanded ? 'chevron_up' : 'chevron_down'}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
    </StyledAccordionButton>
  );
}

export enum AccordionVariants {
  DELIVERY = 'DELIVERY',
  DELIVERY_REDESIGN = 'DELIVERY_REDESIGN',
  DIETARY_FILTERS = 'DIETARY_FILTERS',
}

const variantStyles: Record<AccordionVariants, BoxProps> = {
  [AccordionVariants.DELIVERY]: {
    borderColor: Color.ColdGrey_100,
    borderWidth: 0.5,
    borderStyle: BorderStyle.Solid,
    paddingV: [4, null, 8],
    paddingH: [4, null, 8],
  },
  [AccordionVariants.DELIVERY_REDESIGN]: {
    borderColor: Color.ColdGrey_100,
    borderWidth: 0.5,
    borderStyle: BorderStyle.Solid,
    paddingV: [4, null, 4],
    paddingH: [4, null, 4],
  },
  [AccordionVariants.DIETARY_FILTERS]: {
    paddingH: 2,
  },
};

type AccordionProps = {
  title: React.ReactNode;
  id: string;
  children: React.ReactNode;
  initialIsExpanded?: boolean;
  variant?: AccordionVariants;
};

function AccordionContainer(props: AccordionProps & Omit<BoxProps, keyof AccordionProps>) {
  const { title, children, variant, ...rest } = props;

  const accordionVariant = variant || AccordionVariants.DELIVERY;
  const containerVariantStyles = variantStyles[accordionVariant];

  const {
    value: { isExpanded, buttonId, contentId },
  } = React.useContext(AccordionContext);

  return (
    <Box
      data-testid="accordion-container"
      {...rest}
      {...containerVariantStyles}
      bg={Color.White}
      position={DisplayPosition.Relative}
    >
      {title}
      <Box
        display={isExpanded ? Display.Block : Display.None}
        role="region"
        aria-labelledby={buttonId}
        id={contentId}
        hidden={!isExpanded}
      >
        {children}
      </Box>
    </Box>
  );
}

export function Accordion(props: AccordionProps & Omit<BoxProps, keyof AccordionProps>) {
  const { children, id, initialIsExpanded, ...rest } = props;
  const [isExpanded, setIsExpanded] = React.useState(initialIsExpanded || false);

  const buttonId = `${id}-button`;
  const contentId = `${id}-content`;

  return (
    <AccordionContext.Provider
      value={{
        value: { isExpanded, buttonId, contentId },
        setIsExpanded,
      }}
    >
      <AccordionContainer id={id} {...rest}>
        {children}
      </AccordionContainer>
    </AccordionContext.Provider>
  );
}
